import { Recording } from "@/models/recording";
import { RecordStatusEnum } from "@/pages/RecordsList/types";
import {
  AuditIcon,
  CheckIcon,
  CloudUploadIcon,
  Color,
  IconSize,
  InformationFilledIcon,
  MicOnIcon,
  PlayerPauseIcon,
  PlayerPlayIcon,
  QuestionmarkFilledIcon,
} from "@enxcs/dap-ui-stratos";
import { FC } from "react";
import { useTranslation } from "react-i18next";
import { StatusContainer, StatusEnum, StatusText } from "./RecordingStatus.styles";
export interface RecordStatusProps {
  record: Recording;
}

const RecordStatus: FC<RecordStatusProps> = ({ record }) => {
  const { t } = useTranslation();

  const { status, isUploadInterrupted } = record;

  const processingStatuses = new Set([RecordStatusEnum.NONE, RecordStatusEnum.IN_PROGRESS, RecordStatusEnum.RETRYING]);
  const readyStatuses = new Set([RecordStatusEnum.DONE, RecordStatusEnum.READY_TO_EDIT, RecordStatusEnum.READY_TO_EXPORT]);
  const isPaused = status === RecordStatusEnum.PAUSED;
  const isRecording = status === RecordStatusEnum.RECORDING;
  const hasFailed = status === RecordStatusEnum.FAILED;
  const isreadyForRecording = status === RecordStatusEnum.READY_FOR_RECORDING;
  const isGenerated = record.status === RecordStatusEnum.GENERATED;

  const getStatus = () => {
    if (isUploadInterrupted) {
      return {
        icon: <InformationFilledIcon size={IconSize.V16} tone={Color.Red.V30} />,
        statusName: t("status.interrupted"),
        name: StatusEnum.INTERRUPTED,
      };
    }
    if (processingStatuses.has(status)) {
      return {
        icon: <CloudUploadIcon size={IconSize.V16} tone={Color.Orange.V40} />,
        statusName: t("status.inProgress"),
        name: StatusEnum.PROCESSING,
      };
    }

    if (isGenerated) {
      return {
        icon: <AuditIcon size={IconSize.V16} tone={Color.Teal.V40} />,
        statusName: t("status.generated"),
        name: StatusEnum.GENERATED,
      };
    }

    if (readyStatuses.has(status)) {
      return {
        icon: <CheckIcon size={IconSize.V16} tone={Color.KellyGreen.V40} />,
        statusName: t("status.readyForEditing"),
        name: StatusEnum.READY,
      };
    }

    if (isPaused) {
      return {
        icon: <PlayerPauseIcon size={IconSize.V16} tone={Color.Neutral.V70} />,
        statusName: t("status.paused"),
        name: StatusEnum.PAUSED,
      };
    }

    if (isreadyForRecording) {
      return {
        icon: <MicOnIcon size={IconSize.V16} tone={Color.Teal.V30} />,
        statusName: t("status.readyForRecording"),
        name: StatusEnum.READY_FOR_RECORDING,
      };
    }

    if (hasFailed) {
      return {
        icon: <InformationFilledIcon size={IconSize.V16} tone={Color.Red.V30} />,
        statusName: t("status.failed"),
        name: StatusEnum.FAILED,
      };
    }

    if (isRecording) {
      return {
        icon: <PlayerPlayIcon size={IconSize.V16} tone={Color.Blue.V40} />,
        statusName: t("status.recording"),
        name: StatusEnum.RECORDING,
      };
    }

    return {
      icon: <QuestionmarkFilledIcon size={IconSize.V16} tone={Color.Neutral.V70} />,
      statusName: t("status.unknown"),
      name: StatusEnum.UNKNOWN,
    };
  };

  const currentStatus = getStatus();

  return (
    <StatusContainer status={currentStatus.name}>
      {currentStatus.icon}
      <StatusText status={currentStatus.name}>{currentStatus.statusName}</StatusText>
    </StatusContainer>
  );
};

export default RecordStatus;
